<template>
  <div id="app">
    <router-view v-if="isLoginPage" />

    <div v-else class="gym-layout" :class="{ 'sidebar-collapsed': collapsed }">
      <el-container style="height:100vh">
        <!-- 侧边栏 -->
        <el-aside :width="collapsed ? '78px' : '240px'" class="sidebar"
          style="transition:width 0.28s cubic-bezier(0.4,0,0.2,1);overflow:hidden">
          <div class="sidebar-content">
            <!-- Logo区域 -->
            <div class="brand">
              <div class="logo-icon" :class="{ 'logo-icon--custom': !!gymLogo }">
                <img v-if="gymLogo" :src="gymLogo" alt="" class="brand-logo-img" />
                <span v-else class="icon-text">💪</span>
              </div>
              <div class="brand-text" v-show="!collapsed">
                <h2 class="brand-title brand-title-dynamic" :title="gymName">{{ gymName }}</h2>
              </div>
            </div>

            
            <!-- 导航菜单（根据角色动态渲染） -->
            <el-menu :default-active="activeMenu" text-color="#6B7280" active-text-color="#0062FF"
              background-color="transparent" class="gym-menu" :collapse="collapsed" :collapse-transition="false"
              popper-class="gym-menu-collapse-tooltip-root"
              @select="handleMenuSelect">
              <el-menu-item
                v-for="item in navItems"
                :key="item.index"
                :index="item.index"
                class="menu-item-custom"
              >
                <el-icon>
                  <component :is="item.icon" />
                </el-icon>
                <template #title>{{ item.title }}</template>
              </el-menu-item>
            </el-menu>

            <!-- 收起按钮（展开状态显示，靠右留边距） -->
            <button v-if="!collapsed" class="collapse-btn collapse-btn--shrink" @click="collapsed = true" title="收起">
              <el-icon>
                <ArrowLeft />
              </el-icon>
            </button>

            <!-- 展开按钮（折叠状态显示，水平居中） -->
            <button v-if="collapsed" class="collapse-btn collapse-btn--expand" @click="collapsed = false" title="展开">
              <el-icon>
                <ArrowRight />
              </el-icon>
            </button>

          </div>
        </el-aside>

        <!-- 主内容区 -->
        <el-container class="main-container">
          <!-- 顶部导航栏 -->
          <el-header class="top-header">
            <div class="header-left">
              <h3 class="page-title">{{ currentPageTitle }}</h3>
              <div class="breadcrumb">
                <span>首页</span>
                <span class="separator">/</span>
                <span class="current">{{ currentPageTitle }}</span>
              </div>
            </div>
            <div class="header-right">
              <div class="search-box">
                <el-icon class="search-icon">
                  <Search />
                </el-icon>
                <input type="text" placeholder="搜索会员、课程..." />
              </div>
              <div class="header-actions">
                <el-popover
                  placement="bottom-end"
                  trigger="click"
                  :width="320"
                  popper-class="notice-popover"
                >
                  <template #reference>
                    <div class="action-btn">
                      <el-icon>
                        <Bell />
                      </el-icon>
                      <span v-if="unreadNoticeCount > 0" class="badge">{{ unreadNoticeCount }}</span>
                    </div>
                  </template>
                  <div class="notice-panel">
                    <div class="notice-title">通知</div>
                    <div v-if="noticeList.length === 0" class="notice-empty">暂无通知</div>
                    <div
                      v-for="notice in noticeList"
                      :key="notice.id"
                      class="notice-item"
                    >
                      <p class="notice-text">{{ notice.text }}</p>
                      <p class="notice-time">{{ notice.time }}</p>
                    </div>
                  </div>
                </el-popover>
                <el-dropdown trigger="click" @command="handleUserCommand">
                  <div class="user-profile">
                    <div class="user-avatar-wrap">
                      <img v-if="userAvatar" :src="userAvatar" alt="User" />
                      <div v-else class="user-avatar-placeholder">
                        {{ userName.charAt(0).toUpperCase() }}
                      </div>
                    </div>
                    <div class="user-info">
                      <span class="user-name">{{ userName || '用户' }}</span>
                      <span class="user-role-badge" :class="'role-' + userRole">{{ roleLabel }}</span>
                    </div>
                    <el-icon class="user-arrow"><ArrowDown /></el-icon>
                  </div>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <el-dropdown-item
                        command="profile"
                        :disabled="userRole !== 'member' && userRole !== 'coach'"
                      >
                        <el-icon><User /></el-icon> 个人信息
                      </el-dropdown-item>
                      <el-dropdown-item divided command="logout">
                        <el-icon><SwitchButton /></el-icon> 退出登录
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </div>
            </div>
          </el-header>

          <!-- 页面内容 -->
          <el-main class="main-content">
            <router-view />
          </el-main>
        </el-container>
      </el-container>
    </div>
  </div>
</template>

<script>
import {
  Calendar, DataAnalysis, Notebook, Setting, Stopwatch,
  Tools, User, UserFilled, Search, Bell, ArrowLeft, ArrowRight,
  ArrowDown, SwitchButton,
} from '@element-plus/icons-vue';
import { ElMessageBox, ElMessage } from 'element-plus';
import api from '@/api/request';

export default {
  name: 'App',
  components: {
    Calendar, DataAnalysis, Notebook, Setting, Stopwatch,
    Tools, User, UserFilled, Search, Bell, ArrowLeft, ArrowRight,
    ArrowDown, SwitchButton,
  },
  data() {
    return {
      collapsed: false,
      noticeList: [],
      noticeTimer: null,
    };
  },
  mounted() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
    this.$store.dispatch('fetchBranding');
    this.loadNotifications();
    window.addEventListener('gym-refresh-notices', this.loadNotifications);
    this.noticeTimer = setInterval(() => {
      this.loadNotifications();
    }, 600000);
  },
  beforeUnmount() {
    window.removeEventListener('gym-refresh-notices', this.loadNotifications);
    window.removeEventListener('resize', this.checkMobile);
    if (this.noticeTimer) {
      clearInterval(this.noticeTimer);
      this.noticeTimer = null;
    }
  },
  computed: {
    isLoginPage() {
      return this.$route && this.$route.path === '/login';
    },
    activeMenu() {
      return this.$route ? this.$route.path : '/';
    },
    currentPageTitle() {
      const map = {
        '/': '数据概览',
        '/member': '会员管理',
        '/coach': '教练管理',
        '/course': '课程管理',
        '/coach-members': '我的学员',
        '/coach-courses': '我的课程',
        '/appointment': '预约管理',
        '/equipment': '器材管理',
        '/checkin': '打卡记录',
        '/profile': '个人中心',
        '/coach-profile': '个人中心',
        '/admin': '系统设置',
        '/member-courses': '课程浏览',
        '/member-coaches': '教练团队',
        '/member-equipment': '器材查看',
        
      };
      return this.$route ? (map[this.$route.path] || '概览') : '概览';
    },
    // 从 store 获取当前用户信息
    userName() {
      return this.$store.getters.userName;
    },
    userRole() {
      return this.$store.getters.userRole;
    },
    userAvatar() {
      return this.$store.getters.userAvatar;
    },
    gymName() {
      return this.$store.getters.gymName;
    },
    gymLogo() {
      return this.$store.getters.gymLogo;
    },
    roleLabel() {
      return this.$store.getters.roleLabel;
    },
    // 根据角色动态渲染的菜单项
    navItems() {
      return this.$store.getters.navItems;
    },
    unreadNoticeCount() {
      return this.noticeList.length;
    },
  },
  methods: {
    checkMobile() {
      this.collapsed = window.innerWidth <= 768;
    },
    handleMenuSelect(index) {
      if (this.$route.path !== index) {
        this.$router.push(index);
      }
    },
    handleUserCommand(command) {
      if (command === 'profile') {
        if (this.userRole === 'member') {
          this.$router.push('/profile');
        } else if (this.userRole === 'coach') {
          this.$router.push('/coach-profile');
        }
        return;
      }
      if (command === 'logout') {
        ElMessageBox.confirm('确定要退出登录吗？', '提示', {
          confirmButtonText: '退出',
          cancelButtonText: '取消',
          type: 'warning',
        }).then(() => {
          this.$store.dispatch('logout');
          ElMessage.success('已退出登录');
          this.$router.push('/login');
        }).catch(() => {});
      }
    },
    async loadNotifications() {
      try {
        const res = await api.getNotificationList({ limit: 20 });
        if (res.data?.code === 200 && Array.isArray(res.data.data)) {
          this.noticeList = res.data.data.map((item) => ({
            id: item.id,
            text: item.text,
            time: item.timeLabel || item.createTime || '',
          }));
        }
      } catch {
        this.noticeList = [];
      }
    },
  },
};
</script>

<style>
:root {
  /* 品牌主色 - 商务活力风 */
  --primary-blue: #0062FF;
  --primary-blue-light: #3D8BFF;
  --primary-blue-dark: #0052D9;

  /* 功能色 */
  --success-green: #10B981;
  --warning-orange: #F59E0B;
  --danger-red: #EF4444;
  --info-cyan: #06B6D4;

  /* 背景色 - 浅色系统 */
  --bg-base: #F5F7FA;
  --bg-white: #FFFFFF;
  --bg-gray: #F9FAFB;
  --bg-elevated: #FAFBFC;

  /* 文字颜色 */
  --text-primary: #1F2937;
  --text-secondary: #6B7280;
  --text-muted: #9CA3AF;
  --text-white: #FFFFFF;

  /* 边框和分割线 */
  --border-color: #E5E7EB;
  --border-light: #F3F4F6;
  --divider: #E5E7EB;

  /* 阴影 - 弥散投影 */
  --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.05);
  --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.08);
  --shadow-glow: 0 0 20px rgba(0, 98, 255, 0.15);

  /* 渐变 */
  --gradient-primary: linear-gradient(135deg, #0062FF 0%, #3D8BFF 100%);
  --gradient-success: linear-gradient(135deg, #10B981 0%, #34D399 100%);
  --gradient-bg: linear-gradient(180deg, #FAFBFC 0%, #F5F7FA 100%);

  /* 圆角规范 */
  --radius-sm: 6px;
  --radius-md: 8px;
  --radius-lg: 12px;
  --radius-xl: 16px;

  /* 间距规范 */
  --spacing-xs: 8px;
  --spacing-sm: 12px;
  --spacing-md: 16px;
  --spacing-lg: 20px;
  --spacing-xl: 24px;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  margin: 0;
  font-family: 'Inter', 'PingFang SC', 'Microsoft YaHei', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: var(--bg-base);
  color: var(--text-primary);
  overflow-x: hidden;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-size: 14px;
  line-height: 1.6;
}

#app {
  min-height: 100vh;
  background: var(--bg-base);
  position: relative;
}

/* 背景装饰效果 - 商务风格 */
#app::before {
  content: '';
  position: fixed;
  top: -10%;
  right: -5%;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(0, 98, 255, 0.03) 0%, transparent 70%);
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;
}

#app::after {
  content: '';
  position: fixed;
  bottom: -10%;
  left: -5%;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(16, 185, 129, 0.02) 0%, transparent 70%);
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;
}

/* 滚动条样式 */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: var(--bg-gray);
}

::-webkit-scrollbar-thumb {
  background: #D1D5DB;
  border-radius: 3px;
  transition: background 0.3s ease;
}

::-webkit-scrollbar-thumb:hover {
  background: #9CA3AF;
}

/* 通用动画 */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.6;
  }
}

@keyframes glow {

  0%,
  100% {
    box-shadow: 0 0 5px rgba(0, 98, 255, 0.2);
  }

  50% {
    box-shadow: 0 0 20px rgba(0, 98, 255, 0.4);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ========== 侧边栏折叠相关 ========== */
.sidebar {
  background: var(--bg-white);
  border-right: 1px solid var(--border-color);
  box-shadow: var(--shadow-md);
  position: relative;
  overflow: hidden;
}

.sidebar-content {
  position: relative;
  z-index: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  width: 240px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 14px;
  border-bottom: 1px solid var(--border-light);
  min-height: 68px;
  overflow: hidden;
  white-space: nowrap;
  flex-shrink: 0;
}

.logo-icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  background: var(--gradient-primary);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 98, 255, 0.25);
  overflow: hidden;
}

.logo-icon--custom {
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.brand-logo-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.brand-title-dynamic {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
  max-height: 2.6em;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
}

.icon-text {
  font-size: 20px;
}

.brand-text {
  overflow: hidden;
}

.brand-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  letter-spacing: 0.3px;
}

.brand-title .highlight {
  color: var(--primary-blue);
}

.brand-subtitle {
  font-size: 11px;
  color: var(--text-muted);
  margin: 0;
  letter-spacing: 0.5px;
}

.collapse-btn-wrap {
  padding: 8px 12px;
  border-bottom: 1px solid var(--border-light);
  display: flex;
  justify-content: flex-end;
}

.expand-btn-wrap {
  padding: 8px 0;
  border-bottom: 1px solid var(--border-light);
  display: flex;
  justify-content: center;
}

.ml-auto {
  margin-left: auto;
}

/* 收起按钮：靠右且与右边缘留出间距 */
.collapse-btn--shrink {
  margin-bottom: 25px;
  margin-left: auto;
  margin-right: 25px;
}

/* 展开按钮：折叠侧边栏中水平居中 */
.collapse-btn--expand {
  margin-bottom: 25px;
  margin-left: 25px;
}


.collapse-btn {
  width: 28px;
  height: 28px;
  background: var(--bg-gray);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-secondary);
  transition: all 0.2s ease;
  font-size: 14px;
}

.collapse-btn:hover {
  background: var(--primary-blue);
  border-color: var(--primary-blue);
  color: white;
}

/* 折叠侧栏菜单项旁的标题气泡：完全不显示（:has 排除带子菜单的弹出层） */
.gym-menu-collapse-tooltip-root.el-popper:not(:has(.el-menu--popup)) {
  display: none !important;
  visibility: hidden !important;
  pointer-events: none !important;
}

.gym-menu {
  flex: 1;
  border: none !important;
  padding: 8px 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.gym-menu::-webkit-scrollbar {
  display: none;
}

.menu-item-custom {
  margin: 2px 8px !important;
  border-radius: var(--radius-md) !important;
  height: 44px !important;
  line-height: 44px !important;
  transition: all 0.25s ease !important;
  position: relative;
}

.menu-item-custom::before {
  content: '';
  position: absolute;
  left: 0;
  top: 6px;
  bottom: 6px;
  width: 3px;
  background: var(--primary-blue);
  transform: scaleY(0);
  transition: transform 0.25s ease;
  border-radius: 0 2px 2px 0;
}

.menu-item-custom:hover {
  background: rgba(0, 98, 255, 0.06) !important;
}

.menu-item-custom.is-active {
  background: rgba(0, 98, 255, 0.1) !important;
  color: var(--primary-blue) !important;
}

.menu-item-custom.is-active::before {
  transform: scaleY(1);
}

.menu-item-custom .el-icon {
  font-size: 18px;
}

/* 折叠时菜单图标居中 */
.el-menu--collapse {
  width: calc(var(--el-menu-icon-width) + var(--el-menu-base-level-padding) * 2.3) !important;
}

.el-menu--collapse .menu-item-custom {
  margin: 2px 4px !important;
  padding: 0 !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
}

.el-menu--collapse .menu-item-custom .el-icon {
  margin: 0 !important;
}

.sidebar-footer {
  padding: 14px 16px;
  border-top: 1px solid var(--border-light);
  background: var(--bg-gray);
}

.stats-mini {
  display: flex;
  align-items: center;
  justify-content: space-around;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-value {
  font-size: 18px;
  font-weight: 700;
  color: var(--primary-blue);
  font-family: 'Inter', sans-serif;
}

.stat-label {
  font-size: 11px;
  color: var(--text-muted);
  margin-top: 2px;
}

.stat-divider {
  width: 1px;
  height: 28px;
  background: var(--border-color);
}

.main-container {
  background: transparent;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.top-header {
  background: var(--bg-white);
  border-bottom: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  height: 64px !important;
  box-shadow: var(--shadow-sm);
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  white-space: nowrap;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--text-muted);
}

.breadcrumb .separator {
  color: var(--border-color);
}

.breadcrumb .current {
  color: var(--primary-blue);
  font-weight: 500;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

.search-box {
  display: flex;
  align-items: center;
  background: var(--bg-gray);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  padding: 0 12px;
  height: 38px;
  min-width: 240px;
  gap: 8px;
  transition: all 0.3s ease;
}

.search-box:focus-within {
  border-color: var(--primary-blue);
  background: var(--bg-white);
  box-shadow: 0 0 0 3px rgba(0, 98, 255, 0.08);
}

.search-icon {
  color: var(--text-muted);
  font-size: 16px;
}

.search-box input {
  flex: 1;
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-size: 14px;
  outline: none;
}

.search-box input::placeholder {
  color: var(--text-muted);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.action-btn {
  position: relative;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-gray);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 0.3s ease;
}

.action-btn:hover {
  background: var(--bg-white);
  border-color: var(--primary-blue);
}

.action-btn .el-icon {
  font-size: 20px;
  color: var(--text-secondary);
}

.notice-panel {
  max-height: 300px;
  overflow-y: auto;
}

.notice-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.notice-empty {
  font-size: 13px;
  color: var(--text-muted);
  padding: 8px 0;
}

.notice-item {
  padding: 10px 0;
  border-bottom: 1px solid var(--border-light);
}

.notice-item:last-child {
  border-bottom: none;
}

.notice-text {
  margin: 0 0 4px;
  font-size: 13px;
  color: var(--text-primary);
  line-height: 1.5;
}

.notice-time {
  margin: 0;
  font-size: 12px;
  color: var(--text-muted);
}

.badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: var(--danger-red);
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 5px;
  border-radius: 10px;
  box-shadow: var(--shadow-sm);
  min-width: 18px;
  text-align: center;
  line-height: 14px;
}

.user-profile {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px 4px 4px;
  background: var(--bg-gray);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 0.3s ease;
  outline: none;
}

.user-profile:hover {
  background: var(--bg-white);
  border-color: var(--primary-blue);
}

.user-avatar-wrap img {
  width: 30px;
  height: 30px;
  border-radius: var(--radius-sm);
  object-fit: cover;
  border: 2px solid var(--primary-blue);
  display: block;
}

.user-avatar-placeholder {
  width: 30px;
  height: 30px;
  border-radius: var(--radius-sm);
  background: var(--gradient-primary);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.user-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
}

.user-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.2;
}

.user-role-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 10px;
  line-height: 1.6;
}

.role-admin {
  background: rgba(0, 98, 255, 0.1);
  color: var(--primary-blue);
}

.role-coach {
  background: rgba(16, 185, 129, 0.1);
  color: var(--success-green);
}

.role-member {
  background: rgba(245, 158, 11, 0.1);
  color: var(--warning-orange);
}

.user-arrow {
  font-size: 12px;
  color: var(--text-muted);
  margin-left: 2px;
}

.el-main.main-content {
  --el-main-padding: 15px;
  overflow-y: auto;
  background: var(--bg-base);
  flex: 1;
}

/* 响应式布局适配 */
@media (max-width: 768px) {
  .breadcrumb {
    display: none;
  }
  .search-box {
    display: none;
  }
  .el-main.main-content {
    --el-main-padding: 12px;
  }
  .header-left {
    gap: 10px;
  }
}

@media (max-width: 480px) {
  .user-info {
    display: none;
  }
  .user-profile {
    padding: 4px;
    gap: 0;
  }
  .user-arrow {
    display: none;
  }
}

/* ===== 全局弹窗移动端适配 ===== */
@media (max-width: 600px) {
  /* 弹窗宽度自适应：!important 可覆盖 Element Plus 的内联 style="width:520px" */
  .el-dialog {
    width: 92vw !important;
    max-width: 92vw !important;
    margin: 0 !important;
    border-radius: 12px !important;
  }

  /* 弹窗叠加层垂直居中 */
  .el-overlay-dialog {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 16px !important;
  }

  /* 弹窗内边距收窄 */
  .el-dialog__header {
    padding: 16px 16px 12px !important;
  }
  .el-dialog__body {
    padding: 12px 16px !important;
    max-height: 70vh !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch !important;
  }
  .el-dialog__footer {
    padding: 10px 16px 16px !important;
  }

  /* 弹窗标题字号 */
  .el-dialog__title {
    font-size: 16px !important;
  }

  /* 表单标签切换为顶部模式（EP2 使用 flex，需设置 flex-direction） */
  .el-dialog .el-form-item {
    flex-direction: column !important;
    align-items: flex-start !important;
    margin-bottom: 14px !important;
  }
  .el-dialog .el-form-item__label {
    width: auto !important;
    text-align: left !important;
    line-height: 1.4 !important;
    padding: 0 0 6px !important;
    font-size: 13px !important;
  }
  .el-dialog .el-form-item__content {
    width: 100% !important;
    margin-left: 0 !important;
  }

  /* 弹窗底部按钮行：两个按钮均分宽度 */
  .el-dialog .dialog-footer {
    display: flex !important;
    gap: 10px !important;
  }
  .el-dialog .dialog-footer button {
    flex: 1 !important;
    justify-content: center !important;
    min-height: 40px !important;
    font-size: 14px !important;
  }

  /* el-select / el-date-picker 全宽 */
  .el-dialog .el-select,
  .el-dialog .el-date-editor {
    width: 100% !important;
  }
}
</style>
